import React from 'react';
import Highlighter from 'react-highlight-words';
import debounce from 'lodash/debounce';

import { connect } from 'react-redux';
import { ThunkDispatch } from 'redux-thunk';
import { Action } from 'redux';
import { Select, Spin, AutoComplete } from 'antd';
import { SelectValue, RefSelectProps } from 'antd/lib/select';
import { OptionGroupData } from 'rc-select/lib/interface';
import { FormattedMessage } from 'react-intl';
import { isEmpty } from 'lodash';

import { ICarLocationsState, getCarsLocations, carsLocationsActions, ILoginState, ICarLocation } from '@share/store/slices';
import { RootState } from '@share/utils';
import { CAR_RECENT_SEARCHES_LABEL, ENTER_KEY } from '@share/constants';
import { LocationsTypes, ILocation } from '@share/common-types';

import { HotelSvg, PlaneSvg, ClearSvg, LocationGreySvg } from '@share/assets';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faLocationDot, faShip } from '@fortawesome/free-solid-svg-icons';

import './style.scss';

interface IMapStateToProps {
  carsLocationsStore: ICarLocationsState;
  loginStore: ILoginState;
}

interface IMapDispatchToProps {
  getCarsLocations: (search: string, selectedLocation: ILocation | undefined, onlyCities: boolean) => void;
  setLocations: (locations: ILocation[]) => void;
  setLoading: () => void;
  clearError: () => void;
}

interface IProps extends IMapDispatchToProps, IMapStateToProps {
  location: ICarLocation;
  title: string;
  placeholder: string;
  type: string;
  display: boolean;

  isExperiences?: boolean;
  disable?: boolean;
  displayError?: boolean;

  setSelectLocation: (location: ILocation) => void;
  setSelectLocationLabel: (locationLabel: string) => void;
}

interface IState {
  isFocused: boolean;
  isDropdownOpen: boolean;
}

const zero = 0;
const MIN_SEARCH_LENGTH = 3;
const LIST_HEIGHT = 500;
const SEARCH_DEBOUNCE = 500;
const { Option, OptGroup } = Select;

class CarsLocationsAutoCompleteComponent extends React.Component<IProps, IState> {
  selectRef: React.RefObject<RefSelectProps> = React.createRef();
  wrapperRef: React.RefObject<HTMLDivElement> = React.createRef();
  state: IState = {
    isFocused: false,
    isDropdownOpen: false,
  };
  lastSearch = '';
  lastSelectedLocation: ILocation;

  onFocus = () => {
    const { isFocused } = this.state;

    if (!isFocused) {
      this.onSearch(this.props.location.selectedLocationLabel || '');
    }

    this.setState({ isFocused: true });
  };

  onChange = (value: SelectValue, option: OptionGroupData) => {
    const {
      carsLocationsStore,
      setSelectLocation,
      setSelectLocationLabel,
      setLocations,
      clearError,
    } = this.props;
    const { locations } = carsLocationsStore;

    const valueParsed = value as string;
    const l = locations.find(({ code }) => code === (option?.key as number));
    const location = !isEmpty(valueParsed) ? { ...l } : null;
    if (location?.type === LocationsTypes.Recents) {
      location.type = location.realType;
    }

    setSelectLocationLabel(valueParsed);
    setSelectLocation(location);

    this.setState({ isDropdownOpen: false, isFocused: true });
    
    setLocations([]);
    this.lastSelectedLocation = location;

    if (option?.key) {
      clearError();
    }
  };

  debouncedSearch = debounce((search: string) => {
    this.props.getCarsLocations(search, this.props.location.selectedLocation, this.props.isExperiences);
  }, SEARCH_DEBOUNCE);

  onSearch = (search: string) => {
    this.lastSearch = search;

    const recentSearchesStorage = localStorage.getItem(CAR_RECENT_SEARCHES_LABEL);
    const recentSearchesList = !isEmpty(recentSearchesStorage)? JSON.parse(recentSearchesStorage) : [];
    let recentSearchesLocations: any[] = [];
    if (recentSearchesList?.length) {
      const recentLocations = recentSearchesList
                                  .map((search: any) => ({ ...search.pickUpLocation, realType: search.pickUpLocation.type, type: LocationsTypes.Recents }))
                                  .filter((location: any, index: number, array: any[]) => array.findIndex(v => (v.code === location.code)) === index)
                                  .slice(0, 5);
      recentSearchesLocations = [...recentLocations];
    }

    this.setState({ isDropdownOpen: !!search || (search === '' && recentSearchesLocations?.length > 0) });

    if (search.length >= MIN_SEARCH_LENGTH) {
      this.props.setLoading();
      this.debouncedSearch(search);
    } else {
      this.props.setLocations([...recentSearchesLocations]);
    }
  };

  focusSelect = () => {
    if (this.selectRef && this.selectRef.current) {
      this.selectRef.current.focus();
    }
  };

  getGroupedLocation = (): {
    destinations: ILocation[];
    airports: ILocation[];
    ports: ILocation[];
    recents: ILocation[];
  } => {
    const { isExperiences, carsLocationsStore } = this.props;
    const { locations } = carsLocationsStore;
    const destinations: ILocation[] = [];
    const airports: ILocation[] = [];
    const ports: ILocation[] = [];
    const recents: ILocation[] = [];

    locations.forEach((location: ILocation) => {
      switch (location.type) {
        case LocationsTypes.Airport: {
          airports.push(location);
          break;
        }
        case LocationsTypes.Destination: {
          destinations.push(location);
          break;
        }
        case LocationsTypes.Port: {
          ports.push(location);
          break;
        }
        case LocationsTypes.Recents: {
          if (!isExperiences || [LocationsTypes.Destination, LocationsTypes.Recents].includes(location.realType)) {
            recents.push({ ...location, type: location.realType });
          }
          break;
        }
        default:
          return null;
      }
    });

    return {
      destinations,
      airports,
      ports,
      recents
    };
  };

  getOptionGroup = (
    array: ILocation[],
    label: string,
    icon: React.ReactNode,
  ): React.ReactNode | null => {
    return array.length ? (
      <OptGroup label={<FormattedMessage id={label} />}>
        {array.map((location: ILocation) => {
          const inner = (
            <>
              <span className="locations-dropdown__option-icon">{icon}</span>
              <span className="locations-dropdown__location-name">
                <Highlighter
                  highlightClassName="bold-text"
                  searchWords={[this.lastSearch.trim()]}
                  autoEscape={true}
                  textToHighlight={location.name}
                />
              </span>
            </>
          );

          return location.type === LocationsTypes.Hotel ? (
            <Option
              key={location.code}
              value={location.name}
              className="locations-dropdown__option hotel"
            >
              <div className="locations-dropdown__option-wrapper">{inner}</div>
            </Option>
          ) : (
            <Option key={location.code} value={location.name} className="locations-dropdown__option">
              {inner}
            </Option>
          );
        })}
      </OptGroup>
    ) : null;
  };

  getDropdown = (menu: React.ReactElement): React.ReactElement => {
    const { carsLocationsStore, location } = this.props;
    const { locations, loading } = carsLocationsStore;
    const { selectedLocationLabel } = location;
    
    const isNothingFound =
      loading || !!locations.length || selectedLocationLabel?.length < MIN_SEARCH_LENGTH;

    return (
      <div className="locations-dropdown">
        {menu}
        {isNothingFound && (
          <div className="locations-dropdown__footer">
            <FormattedMessage id="search.more.label" />
          </div>
        )}
      </div>
    );
  };

  handleClickOutside = (event: MouseEvent) => {
    if (this.state.isDropdownOpen) {
      if (
        (this.wrapperRef && !this.wrapperRef.current.contains(event.target as Node)) ||
        (event.target as HTMLElement).classList.contains(`cars-locations-picker__fixed-bg-${this.props.type}`)
      ) {
        this.setState({ isFocused: false, isDropdownOpen: false });
        this.props.setLocations([]);
        this.lastSearch = '';
      }
    } else {
      this.setState({ isFocused: false });
    }
  };

  componentDidMount() {
    document.addEventListener("mousedown", this.handleClickOutside);
  }

  componentWillUnmount() {
    document.removeEventListener("mousedown", this.handleClickOutside);
  }

  getPopupContainer = () => {
    return this.wrapperRef ? this.wrapperRef.current : document.body;
  };

  getNotFoundContent = (): React.ReactNode => {
    const { carsLocationsStore, location } = this.props;
    const { loading } = carsLocationsStore;
    const { selectedLocationLabel } = location;
    let message: React.ReactNode;

    if (selectedLocationLabel?.length < MIN_SEARCH_LENGTH) {
      message = <FormattedMessage id="search.min.symbols" />;
    } else if (loading) {
      message = <FormattedMessage id="searching" />;
    } else {
      message = <FormattedMessage id="nothing.found" />;
    }

    return (
      <div className="locations-dropdown__nothing-found">
        {message}
      </div>);
  };

  onClick = (event: React.MouseEvent<HTMLElement>) => {
    const {
      carsLocationsStore,
      location,
      setLocations,
      setSelectLocationLabel,
      setSelectLocation,
      clearError,
    } = this.props;
    const { locations } = carsLocationsStore;
    const { selectedLocationLabel } = location;
    const { isFocused, isDropdownOpen } = this.state;

    if (
      selectedLocationLabel &&
      isFocused &&
      !isDropdownOpen &&
      event.target &&
      (event.target as HTMLElement).classList.contains('ant-select-selection-search-input')
    ) {
      this.onSearch(selectedLocationLabel);
    } else if (
      (event.target as HTMLElement).classList.contains('ant-select-item-option-selected') ||
      (event.target as HTMLElement).closest('.ant-select-item-option-selected')
    ) {
      if (this.lastSelectedLocation) {
        this.setState({ isDropdownOpen: false, isFocused: true });
        setLocations([]);
      } else {
        const location = locations.find(
          ({ name }) => name.toLowerCase() === selectedLocationLabel.toLowerCase(),
        );

        if (location) {
          this.setState({ isDropdownOpen: false, isFocused: true });
          setSelectLocationLabel(location.name);
          setSelectLocation(location);
          setLocations([]);
          this.lastSelectedLocation = location;
          clearError();
        }
      }
    }
  };

  onKeyDown = (e: React.KeyboardEvent) => {
    const { isDropdownOpen, isFocused } = this.state;
    const {
      carsLocationsStore,
      setSelectLocationLabel,
      setSelectLocation,
      setLocations,
      clearError,
    } = this.props;
    const { locations } = carsLocationsStore;

    if (
      e.code === ENTER_KEY &&
      isDropdownOpen &&
      isFocused &&
      locations.length &&
      !this.lastSelectedLocation
    ) {
      const item = locations[zero];

      this.setState({ isDropdownOpen: false, isFocused: true });
      setSelectLocationLabel(item.name);
      setSelectLocation(item);
      setLocations([]);
      this.lastSelectedLocation = item;

      if (item) {
        clearError();
      }
    }
  };

  render(): React.ReactNode {
    const { location, loginStore, disable, display, displayError, title, placeholder, type, carsLocationsStore } = this.props;
    const { selectedLocationLabel } = location;
    const { isFocused, isDropdownOpen } = this.state;
    const { airports, destinations, ports, recents } = this.getGroupedLocation();
    const { loading, error } = carsLocationsStore;

    const account = loginStore?.account;
    const color: any = account?.searchIconsColor;

    const styleColor = !isEmpty(color)? { color } : {};  
    
    if (!display) {
      return null;
    }

    return (
      <div className={`cars-locations-picker__container ${(!!error && displayError) ? 'error' : ''}`} ref={this.wrapperRef}>
        <div
          className={`cars-locations-picker__wrapper ${isFocused ? 'selected' : ''}`}
          onClick={this.focusSelect}
        >
          
          <div className="cars-locations-picker__left">
            <div className="cars-locations-picker__icon" style={styleColor}>
              <FontAwesomeIcon icon={faLocationDot} />
            </div>
          </div>
          <div className="cars-locations-picker__right">
            <div className="cars-locations-picker__label">
              <FormattedMessage id={title ? title :'location'} />
            </div>
            <div className="cars-locations-picker__input">
              <AutoComplete
                onKeyDown={this.onKeyDown}
                virtual={false}
                showSearch={true}
                placeholder={<FormattedMessage id={!isEmpty(placeholder) ? placeholder : 'location.input.placeholder'} />}
                onChange={this.onChange}
                onSearch={this.onSearch}
                onFocus={this.onFocus}
                showArrow={false}
                ref={this.selectRef}
                open={isDropdownOpen}
                defaultActiveFirstOption={false}
                filterOption={() => true}
                autoClearSearchValue={false}
                dropdownRender={this.getDropdown}
                getPopupContainer={this.getPopupContainer}
                listHeight={LIST_HEIGHT}
                allowClear={true}
                value={selectedLocationLabel}
                notFoundContent={this.getNotFoundContent()}
                onClick={this.onClick}
                disabled={disable}
                clearIcon={
                  <span className="cars-locations-picker__clear">
                    {loading && isDropdownOpen ? <Spin /> : <ClearSvg />}
                  </span>
                }
              >

                {selectedLocationLabel?.length < MIN_SEARCH_LENGTH ? (
                  <Option key={MIN_SEARCH_LENGTH} value={MIN_SEARCH_LENGTH} disabled={true} className="locations-dropdown__option">
                    <FormattedMessage id="search.min.symbols" />
                  </Option>) : null}
                  
                {this.getOptionGroup(airports, 'airports', <PlaneSvg />)}
                {this.getOptionGroup(destinations, 'destinations', <LocationGreySvg />)}
                {this.getOptionGroup(ports, 'ports', <FontAwesomeIcon icon={faShip} style={{ color: '#D2D2D2' }} />)}
                {this.getOptionGroup(recents, 'recents', <LocationGreySvg />)}

              </AutoComplete>
            </div>
          </div>
        </div>

        {isDropdownOpen ? <div className={`cars-locations-picker__fixed-bg-${type}`} /> : null}
      </div>);
  }
}

const mapStateToProps = (state: RootState): IMapStateToProps => {
  return {
    carsLocationsStore: state.carsLocationsStore,
    loginStore: state.loginStore
  };
};

const mapDispatchToProps = (dispatch: ThunkDispatch<RootState, undefined, Action>): IMapDispatchToProps => ({
  getCarsLocations: (search: string, selectedLocation: ILocation | undefined, onlyCities: boolean) => dispatch(getCarsLocations(search, selectedLocation, onlyCities)),
  setLocations: (locations: ILocation[]) => dispatch(carsLocationsActions.setLocations(locations)),
  setLoading: () => dispatch(carsLocationsActions.setLoading(true)),
  clearError: () => dispatch(carsLocationsActions.setError('')),
});

export const CarsLocationsAutoComplete = connect(mapStateToProps, mapDispatchToProps)(CarsLocationsAutoCompleteComponent);
